import isEmpty from 'lodash/isEmpty'
import keyBy from 'lodash/keyBy'
import toLower from 'lodash/toLower'
import toNumber from 'lodash/toNumber'
import {
  absDependencies,
  addDependencies,
  andDependencies,
  create,
  divideDependencies,
  equalDependencies,
  largerDependencies,
  largerEqDependencies,
  maxDependencies,
  minDependencies,
  multiplyDependencies,
  orDependencies,
  parseDependencies,
  powDependencies,
  roundDependencies,
  smallerDependencies,
  smallerEqDependencies,
  subtractDependencies,
  unequalDependencies,
  unaryMinusDependencies,
} from 'mathjs'
import type { ConstantNode, FunctionNode, MathNode, OperatorNode, SymbolNode } from 'mathjs'
import { PivotAggregationEnum } from '@ztechapps/common.contract'
import { PIVOT_FORMULA_FUNCTIONS } from '../pivot-formula-functions'

// A locked-down mathjs instance: the expression parser plus only the arithmetic, comparison
// and logic operators and the scalar functions a pivot formula may use — never `all`, so the
// pivot bundle carries none of the units, statistics or matrix surface. Built on first use so
// importing the barrel runs no mathjs code.
let mathInstance: ReturnType<typeof create> | undefined
const getMathInstance = (): ReturnType<typeof create> => {
  if (!mathInstance) {
    mathInstance = create({
      parseDependencies,
      addDependencies,
      subtractDependencies,
      unaryMinusDependencies,
      multiplyDependencies,
      divideDependencies,
      powDependencies,
      equalDependencies,
      unequalDependencies,
      smallerDependencies,
      largerDependencies,
      smallerEqDependencies,
      largerEqDependencies,
      andDependencies,
      orDependencies,
      absDependencies,
      roundDependencies,
      minDependencies,
      maxDependencies,
    })
  }
  return mathInstance
}

const PIVOT_FORMULA_AGGREGATIONS: Record<string, PivotAggregationEnum> = keyBy<PivotAggregationEnum>(
  Object.values(PivotAggregationEnum),
  toLower
)
// aggregation calls over a quoted field are already value slots by the time functions are checked, so
// a call left over may only be a scalar function — `min` and `max` double as both
const PIVOT_SCALAR_FUNCTIONS = PIVOT_FORMULA_FUNCTIONS.filter(
  (name) => !(name in PIVOT_FORMULA_AGGREGATIONS) || name === 'min' || name === 'max'
)
const PIVOT_FORMULA_OPERATORS = [
  'add',
  'subtract',
  'unaryMinus',
  'multiply',
  'divide',
  'pow',
  'equal',
  'unequal',
  'smaller',
  'larger',
  'smallerEq',
  'largerEq',
  'and',
  'or',
]

/** One aggregation call a pivot formula references, replaced by a value slot at compile time. */
export type PivotFormulaReference = { field: string; aggregation: PivotAggregationEnum }
/** A compiled pivot formula: its field references plus an evaluator over per-slot values. */
export type PivotFormula = {
  references: PivotFormulaReference[]
  evaluate: (values: (number | undefined)[]) => number | undefined
}

/**
 * Compiles a pivot formula (`sum("net") - sum("gross")`). Each aggregation
 * call over a quoted field becomes a positional value slot recorded in `references`; anything
 * else the pivot cannot evaluate throws an `Error` naming the problem. Compile once per
 * formula — evaluation reuses the closure.
 */
export const compilePivotFormula = (formula: string): PivotFormula => {
  if (isEmpty(formula.trim())) throw new Error('Enter a formula')
  const math = getMathInstance()
  let parsed: MathNode
  try {
    parsed = math.parse(formula)
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : String(error))
  }
  const references: PivotFormulaReference[] = []
  const slotByKey = new Map<string, number>()
  const transformed = parsed.transform((current) => {
    if (current.type !== 'FunctionNode') return current
    const fn = current as FunctionNode
    const name = fn.fn.type === 'SymbolNode' ? fn.fn.name : ''
    const aggregation = PIVOT_FORMULA_AGGREGATIONS[name]
    const onlyArg = fn.args.length === 1 ? fn.args[0] : undefined
    const rawValue = onlyArg?.type === 'ConstantNode' ? ((onlyArg as ConstantNode).value as unknown) : undefined
    if (aggregation === undefined || typeof rawValue !== 'string') return current
    const key = `${rawValue}|${aggregation}`
    let slot = slotByKey.get(key)
    if (slot === undefined) {
      slot = references.length
      slotByKey.set(key, slot)
      references.push({ field: rawValue, aggregation })
    }
    return math.parse(`__v${slot}`)
  })
  // walks the tree like the product spec evaluator
  // (libs/product/node/src/actions/import-product-with-spec/ImportProductWithSpec.ts:300-310)
  transformed.traverse((current, _path, parent) => {
    if (current.type === 'FunctionNode') {
      const fn = current as FunctionNode
      const name = fn.fn.type === 'SymbolNode' ? fn.fn.name : ''
      if (!PIVOT_SCALAR_FUNCTIONS.includes(name)) throw new Error(`Unsupported function ${name}`)
      return
    }
    if (current.type === 'OperatorNode') {
      const fn = (current as OperatorNode).fn as string
      if (!PIVOT_FORMULA_OPERATORS.includes(fn)) throw new Error(`Unsupported operator ${fn}`)
      return
    }
    if (current.type === 'ConstantNode') {
      if (typeof ((current as ConstantNode).value as unknown) !== 'number')
        throw new Error('Quote field names only inside an aggregation, like sum("gross")')
      return
    }
    if (current.type === 'SymbolNode') {
      // a function name reads as its FunctionNode's `fn` child — only a bare name is unknown
      const owner = parent as unknown as FunctionNode | null
      if (owner !== null && owner.type === 'FunctionNode' && owner.fn === current) return
      const name = (current as SymbolNode).name
      if (!/^__v\d+$/.test(name)) throw new Error(`Unknown name ${name}`)
      return
    }
    if (current.type !== 'ParenthesisNode') throw new Error('Unsupported expression')
  })
  const compiled = transformed.compile()
  return {
    references,
    evaluate: (values) => {
      // a missing slot value is not-a-number, matching the pivot's blank handling; `if`
      // without an else and `isNaN` come from the scope — the instance carries neither
      const scope: Record<string, unknown> = {}
      values.forEach((value, index) => {
        scope[`__v${index}`] = value ?? NaN
      })
      scope.if = (condition: unknown, whenTrue: unknown, whenFalse: unknown = NaN) => (condition ? whenTrue : whenFalse)
      scope.isNaN = (value: unknown) => Number.isNaN(toNumber(value))
      const result = toNumber(compiled.evaluate(scope))
      return Number.isFinite(result) ? result : undefined
    },
  }
}
